import { useCallback } from 'react';
import DocumentTitle from 'react-document-title';
import { useNavigate } from 'react-router-dom';
import Sticky from 'react-stickynode';
import styled, { css, useTheme } from 'styled-components';
import { border, color } from 'styled-system';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import {
  responsive,
  separatorShadow,
  useBreakpoint,
} from '@hivebrite/design-system/Theme';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import EventActions from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventActions';
import EventHeader from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventHeader';
import EventInformations from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventInformations';
import EventSummary from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventSummary';
import HighlightedEvents from 'hb-react/shared/apps/Events/components/HighlightedEvents';
import {
  EventProvider,
  useEventContext,
} from 'hb-react/shared/apps/Events/contexts/EventContext';
import { useEvent } from 'hb-react/shared/apps/Events/hooks';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';
import ThemeProvider from 'hb-react/style/ThemeProvider';

export const EventDetailsContainer = () => {
  const { currentUser } = useDefaultAppContexts();
  const { headerHeight } = useUIContext();
  const theme = useTheme();
  const frameTheme = useFrameTheme();
  const { event } = useEventContext();
  const navigate = useNavigate();
  const timeZoneConfiguration = useTimeZoneConfiguration();
  const topicContext = useTopicContext();

  const breakpoint = useBreakpoint();

  const navigateToEvent = useCallback(
    (event) =>
      navigate(
        getEventDedicatedPath({
          hasUser: currentUser,
          event,
          topicId: topicContext.topicId,
          isWhitelabel: !!topicContext.whitelabelURL,
        }),
      ),
    [currentUser, navigate],
  );

  return (
    <Box
      flexDirection="column"
      m="auto"
      maxWidth={{ ...theme.breakpoints, m: '100%' }}
    >
      {/* Using a single Grid made it impossible to add the box-shadow in XL where the design leaves 1 empty column on each side
      so we're using 2 Grids to maintain the proper column/space sizes while giving the InnerGrid the desired drop-shadow */}
      <OuterGrid $topicId={topicContext?.topicId}>
        <Row>
          <OuterCol>
            <ThemeProvider
              theme={frameTheme.themeName}
              fontBrightness={frameTheme.fontBrightness}
            >
              <InnerGrid>
                {/* zIndex: 2 is necessary so the boxShadow of the top-right cell goes over the cells of the second row */}
                <Row
                  css={css`
                    z-index: ${breakpoint === 's' ? 4 : 2};
                  `}
                >
                  <LeftCol>
                    <LeftCell
                      py={0}
                      pl={0}
                      pr={0}
                      height={{ s: 'auto', m: 306, l: 320, xl: 350 }}
                      borderTopLeftRadius={{
                        _: 0,
                        l: theme.themeVariables.globalBorderRadius,
                      }}
                      bg={frameTheme.backgroundColor}
                    >
                      <EventHeader
                        flexGrow={1}
                        height={{ s: 'auto', m: 225, l: 284, xl: 350 }}
                      />
                    </LeftCell>
                  </LeftCol>
                  <RightCol
                    css={css`
                      ${separatorShadow}
                    `}
                    borderTopRightRadius={{
                      _: 0,
                      l: theme.themeVariables.globalBorderRadius,
                    }}
                    bg={frameTheme.backgroundColor}
                  >
                    {/* height 100% required in case the EventSummary content is smaller than the minHeight of the first row (as
                    enforced by the EventHeader height above) to ensure the event category tags are correctly positioned in the
                    bottom-left */}
                    <RightCell pt={4} pb={{ m: 4 }} height="100%">
                      <EventSummary event={event} flexGrow={1} />
                    </RightCell>
                  </RightCol>
                </Row>
                <Row
                  flexDirection={{ l: 'row', m: 'row', s: 'column-reverse' }}
                >
                  {/* zIndex: 2 is necessary so the boxShadow of the virtual event countdown gows over the cell on the right */}
                  <LeftCol
                    css={css`
                      z-index: 2;
                    `}
                  >
                    <LeftCell
                      borderBottomLeftRadius={{
                        _: 0,
                        l: theme.themeVariables.globalBorderRadius,
                      }}
                      bg={frameTheme.backgroundColor}
                    >
                      <EventInformations
                        event={event}
                        flexGrow={1}
                        flexShrink={1}
                      />
                    </LeftCell>
                  </LeftCol>
                  <RightCol
                    id="event-actions-container"
                    borderBottomRightRadius={{
                      _: 0,
                      l: theme.themeVariables.globalBorderRadius,
                    }}
                    bg={frameTheme.backgroundColor}
                  >
                    <Sticky
                      enabled={breakpoint !== 's'}
                      top={currentUser ? headerHeight : 0}
                      bottomBoundary="#event-actions-container"
                    >
                      <RightCell pb={{ s: 0 }} width="100%">
                        <EventActions event={event} />
                      </RightCell>
                    </Sticky>
                  </RightCol>
                </Row>
              </InnerGrid>
            </ThemeProvider>
            {event.show_related_events && (
              <HighlightedEvents
                eventId={event.id}
                headline={event.headline_related_events}
                onEventCardClick={navigateToEvent}
                timezoneConfiguration={timeZoneConfiguration}
              />
            )}
          </OuterCol>
        </Row>
      </OuterGrid>
    </Box>
  );
};

/**
 *  @deprecated
 */
export const EventDetailsPage = ({ config }) => {
  const { isLoading, event } = useEvent();
  const { currentNetwork } = useDefaultAppContexts();

  if (isLoading) {
    return <Loader />;
  }

  if (!event) {
    return null;
  }

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          networkName: getPageTitleFromNetwork(currentNetwork),
          title: event?.title,
        })}
      />
      <EventProvider event={event} config={config}>
        <EventDetailsContainer />
      </EventProvider>
    </>
  );
};

// STYLED COMPONENTS
const OuterGrid = styled(Grid).attrs(({ theme, $topicId }) => ({
  my: { _: 0, m: $topicId ? 9 : 0, l: 10, xl: 10 },
  mx: { ...theme.grid.gridGap, s: 0, m: 0 },
  rowGap: 0,
  colGap: 0,
  // Width of the grid at 1440 browser window width
  width: { xl: 1377 },
  alignSelf: { xl: 'center' },
}))`
  flex: 1;
`;

const OuterCol = styled(Col).attrs(() => ({
  offset: { _: 0, xl: 1 / 12 },
  width: { _: 1, xl: 10 / 12 },
}))`
  width: 100%;
`;

const InnerGrid = styled(Grid).attrs({
  my: 0,
  mx: 0,
  numCols: { _: 12, m: 12, l: 12, xl: 10 },
  rowGap: 0,
})`
  ${border}
  box-shadow: 0 1px 12px 2px rgba(0, 0, 0, 0.1);
  ${responsive('l')`
      border-radius: ${({ theme }) =>
        `${theme.themeVariables.globalBorderRadius}px`};
    `}
`;

const LeftCol = styled(Col).attrs(() => ({
  width: { _: 7 / 12, xl: 6 / 10 },
}))`
  width: 100%;
`;

const RightCol = styled(Col).attrs(() => ({
  width: { _: 5 / 12, xl: 4 / 10 },
}))`
  ${color}
  ${border}
  width: 100%;
`;

// Keep pt/pb instead of py so we can overwrite them individually (<RightCell> around <EventSummary>)
const RightCell = styled(Box).attrs(({ theme, pb, ...otherProps }) => ({
  pt: theme.grid.rowGap,
  pb: { ...theme.grid.rowGap, ...pb },
  pl: { _: theme.grid.colGap._, m: 0, l: 0, xl: 0 },
  pr: theme.grid.colGap,
  flexDirection: 'column',
  ...otherProps,
}))``;

const LeftCell = styled(Box).attrs(({ theme, ...otherProps }) => ({
  py: theme.grid.rowGap,
  pl: { _: theme.grid.colGap._, m: 12 },
  pr: { _: theme.grid.colGap._, m: 0, l: 0, xl: 0 },
  flexDirection: 'column',
  ...otherProps,
}))`
  ${border}
`;
